import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DIALOG_FORM_CLASSES,
  DialogBody,
} from "@/components/ui/dialog";
import { useCloseGuard } from "@/hooks/use-close-guard";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ScopeSelector } from "./scope-selector";
import { ApiKeyDisplayModal } from "./api-key-display-modal";
import { useCreateApiKey } from "@/hooks/useApiKeyMutations";
import { toast } from "sonner";

const createApiKeySchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(200, "Name must be at most 200 characters")
    .regex(
      /^[a-zA-Z0-9_-]+$/,
      "Name can only contain letters, numbers, hyphens, and underscores",
    ),
  scopeCategory: z.string().default("read_only"),
  scopes: z.string().default("[]"),
});

export type CreateApiKeyForm = z.infer<typeof createApiKeySchema>;

interface CreateApiKeyDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CreateApiKeyDialog({ open, onClose }: CreateApiKeyDialogProps) {
  const [createdApiKey, setCreatedApiKey] = useState<{
    key: string;
    name: string;
  } | null>(null);
  const createApiKey = useCreateApiKey();

  const form = useForm<CreateApiKeyForm>({
    resolver: zodResolver(createApiKeySchema),
    defaultValues: {
      name: "",
      scopeCategory: "read_only",
      scopes: JSON.stringify([
        "resources:read",
        "networks:read",
        "snapshots:read",
        "billing:read",
        "tenants:read",
      ]),
    },
  });

  const handleSubmit = async (data: CreateApiKeyForm) => {
    try {
      const result = await createApiKey.mutateAsync({
        name: data.name,
        scopes: data.scopes,
        expiresAt: null,
      });

      setCreatedApiKey({
        key: result.plainTextKey,
        name: result.apiKey.name,
      });
      form.reset();
    } catch (error) {
      console.error("Failed to create API key:", error);
      toast.error("Failed to create API key");
    }
  };

  const handleClose = () => {
    form.reset();
    setCreatedApiKey(null);
    onClose();
  };

  const handleDisplayModalClose = () => {
    setCreatedApiKey(null);
    onClose();
  };

  const guard = useCloseGuard({
    open: open && !createdApiKey,
    onOpenChange: (open) => !open && handleClose(),
    isDirty: form.formState.isDirty,
    isSubmitting: createApiKey.isPending,
    onDiscard: () => form.reset(),
  });

  return (
    <>
      <Dialog open={open && !createdApiKey} onOpenChange={guard.onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className={DIALOG_FORM_CLASSES}
            >
              <DialogBody className="space-y-4">
                <DialogDescription>
                  Create a new API key for programmatic access to the WiCloud
                  API.
                </DialogDescription>
                <FormField
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="my-api-key"
                          {...field}
                          disabled={createApiKey.isPending}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <ScopeSelector name="scopeCategory" />
              </DialogBody>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => guard.onOpenChange(false)}
                  disabled={createApiKey.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createApiKey.isPending}>
                  {createApiKey.isPending ? "Creating..." : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      {guard.dialog}

      {createdApiKey && (
        <ApiKeyDisplayModal
          open={true}
          onClose={handleDisplayModalClose}
          apiKey={createdApiKey.key}
          name={createdApiKey.name}
        />
      )}
    </>
  );
}
